import {
  archesSchema,
  getArchesCoveredExactly,
  getArchFromTooth,
  getTeethFromArch,
  isArchScopedItem,
  ORDERED_ARCHES,
  type ItemType,
  type TeethArch,
} from "@jasper/client-models";
import { TeethSelect, validateSchema } from "@jasper/forms";
import { Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { ConfigurableProduct, ProductStepProps } from "./types";
import type { UpdateProductArgs } from "@jasper/use-cases";
import { useRegisterStepSave } from "./useRegisterStepSave";
import { useTeethSelectionDescription } from "./TeethSteps/useTeethSelectionDescription";

/*
  Validated as an object rather than a bare array: `validateSchema` drops issues whose path
  is empty, so a root-level failure would never reach the form. Same shape as the teeth
  steps, which read their message under the `arches` key.
*/
const archesFormSchema = z.object({ arches: archesSchema });

type Props = ProductStepProps & {
  applyArchesSelection?: (args: {
    teeth: number[];
    product: ConfigurableProduct;
    itemType: ItemType;
  }) => Promise<UpdateProductArgs> | UpdateProductArgs;
};

const defaultApplyArchesSelection = async ({
  teeth,
  product,
  itemType,
}: {
  teeth: number[];
  itemType: ItemType;
  product: ConfigurableProduct;
}) => {
  const arch = getArchFromTooth(teeth[0]);
  const itemsToSave = arch ? [{ type: itemType, arch }] : [];

  return {
    productId: product.id,
    teeth: teeth,
    items: itemsToSave,
  };
};

export const ArchStep = ({
  stepKey,
  product,
  itemType,
  orderConfiguration,
  bulk,
  applyArchesSelection = defaultApplyArchesSelection,
  registerStepSave,
}: Props) => {
  const { t } = useTranslation("product-workflows");
  const describeTeethSelection = useTeethSelectionDescription();
  const { order, updateProduct, addOrderProducts, removeOrderProducts } =
    orderConfiguration;

  /** Same-type products of the order, indexed by the arch their item covers. */
  const siblingProductByArch = useMemo(() => {
    const entries = (order?.products ?? [])
      .filter(
        orderProduct =>
          orderProduct.id !== product.id && orderProduct.type === product.type
      )
      .flatMap(orderProduct => {
        const [archItem] = orderProduct.items.filter(isArchScopedItem);
        return archItem ? [[archItem.arch, orderProduct] as const] : [];
      });

    return new Map(entries);
  }, [order?.products, product.id, product.type]);

  /*
    Every arch the configured product covers, and not just the first: V1 saves a
    bimaxillary product as one product holding two arch items. Reading them all is what
    shows both arches selected, and what lets submitting split them into one product per
    arch, as V2 saves them.
  */
  const ownArches = useMemo(
    () => product.items.filter(isArchScopedItem).map(item => item.arch),
    [product.items]
  );

  /*
    The step owns every arch of the order for this product type, not just the configured
    product's: a bimaxillary wax bite is two sibling products this very step created, so
    reopening it must show both arches selected — otherwise the reconciliation below reads
    the sibling as unselected and deletes it.
  */
  const initialArches = useMemo(
    () => [...ownArches, ...siblingProductByArch.keys()],
    [ownArches, siblingProductByArch]
  );

  const defaultValues = useMemo(
    () => ({
      product: {
        arches: initialArches,
      },
    }),
    [initialArches]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const arches = ORDERED_ARCHES.filter(arch =>
        value.product.arches.includes(arch)
      );
      const validation = validateSchema(archesFormSchema, { arches });

      if (!validation.success) {
        return;
      }

      if (bulk?.isSupported && arches.length > 1) {
        await bulk.onSubmit(
          arches.map(arch => [...getTeethFromArch(arch)]),
          applyArchesSelection
        );
        return;
      }

      /*
        The configured product keeps its own arch as long as it stays selected; on creation it
        has none yet and takes the first arch no sibling covers. The invariant is one product
        per selected arch, so a sibling is removed once its arch is unselected or taken over.
      */
      const freeArches = arches.filter(arch => !siblingProductByArch.has(arch));
      const keptOwnArch = ownArches.find(arch => arches.includes(arch));
      const productArch = keptOwnArch ?? freeArches[0] ?? arches[0];

      const archesToCreate = arches.filter(
        arch => arch !== productArch && !siblingProductByArch.has(arch)
      );
      const productIdsToRemove = [...siblingProductByArch.entries()]
        .filter(([arch]) => !arches.includes(arch) || arch === productArch)
        .map(([, sibling]) => sibling.id);

      /*
        The sibling products are created before the configured product is rewritten: saving
        it replaces its items, so a product created by V1 on both arches loses its second
        item there. Creating first means a failure in the middle leaves a duplicated arch,
        which the next submit reconciles, instead of a destroyed one.
      */
      try {
        if (archesToCreate.length) {
          await addOrderProducts(archesToCreate.map(buildProductArgs));
        }

        await updateProduct({
          productId: product.id,
          ...buildProductArgs(productArch),
        });

        if (productIdsToRemove.length) {
          await removeOrderProducts(productIdsToRemove);
        }
      } catch {
        notifications.show({
          title: t("common.errorNotificationTitle"),
          message: t("common.errorNotificationMessage"),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const validateSelection = ({
    value,
  }: {
    value: { arches: TeethArch[] };
  }): Record<string, unknown> | undefined => {
    const arches = ORDERED_ARCHES.filter(arch => value.arches.includes(arch));
    const result = validateSchema(archesFormSchema, { arches });
    return result.success ? undefined : result.errors;
  };

  const buildProductArgs = (arch: TeethArch) => ({
    type: product.type,
    teeth: getTeethFromArch(arch),
    items: [{ type: itemType, arch }],
  });

  const toggleArch = (arch: TeethArch, currentArches: TeethArch[]) => {
    const newArches = currentArches.includes(arch)
      ? currentArches.filter(selected => selected !== arch)
      : [...currentArches, arch];
    return newArches;
  };

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <Stack gap="md" flex={1} mih={0}>
        <Text c="dimmed" size="sm">
          {t("steps.arch.hint")}
        </Text>
        <form.Field
          name="product"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const errors = validateSelection({ value });

              if (!errors) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return errors;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            const errors =
              typeof formErrors?.arches === "string"
                ? t(formErrors?.arches, {
                    keyPrefix: "steps.arch.errors",
                    defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                  })
                : undefined;

            const teethToShow =
              field.state.value.arches?.flatMap(getTeethFromArch) ?? [];

            return (
              <TeethSelect
                onBlur={() => field.handleBlur()}
                teeth={teethToShow}
                teethError={errors ? teethToShow : []}
                error={errors}
                onClick={toothId => {
                  const arch = getArchFromTooth(toothId);
                  if (arch) {
                    const newArches = toggleArch(
                      arch,
                      field.state.value.arches
                    );
                    field.setValue({
                      ...field.state.value,
                      arches: newArches,
                    });
                  }
                }}
                getTeethDescription={({ teeth }) =>
                  describeTeethSelection(teeth, [
                    {
                      type: itemType,
                      count: getArchesCoveredExactly(teeth).length,
                    },
                  ])
                }
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
